import React from 'react';
import {useRecoilState, useRecoilValue, useRecoilValueLoadable} from 'recoil';
import * as AppState from '../../AppState';
import { UserLocation, UserFilters } from '../../interfaces/interfaces';
import { projectionFieldsToReturn, getBedroomFilterFragment, getBathroomFilterFragment, getPriceFilterFragment, getRangeFilterFragment, getHomeTypeFilterFragment, getMinFilterFragment, getHasGarageFilterFragment, getMaxFilterFragment, addGeoFiltersToQuery, getSearchAreaFilterFragment } from './elasticQueryUtils';

export default function createQuery(userLocation: UserLocation | null, userFilters: UserFilters, pageSize: number, pageNumber: number, propertyListSort: number) {

	if (userLocation === null) return;

	const boolQuery: any = { filter: [], should: [], must: [], must_not: [] };

	boolQuery.must_not.push(
		{
			term: {
				'PropertyType.keyword': 'Residential Lease',
			}
		}
	);
	boolQuery.must_not.push(
		{
			term: {
				'PropertyType.keyword': 'Commercial Lease',
			}
		}
	);

	boolQuery.filter.push(...getFilterFragments(userLocation, userFilters));

	if (userFilters.searchArea !== undefined)
		boolQuery.filter.push(getSearchAreaFilterFragment(userFilters.searchArea));
	else
		addGeoFiltersToQuery(userLocation, boolQuery);

	let sort;

	const defaultSort = [{ 'isCapcenterListing': { 'order': 'desc' } }, { 'DerivedOnMarketTimestamp': { 'order': 'desc' } }];
	const newestSort = [{ 'DerivedOnMarketTimestamp': { 'order': 'desc' } }];
	const priceDescSort = [{ 'ListPrice': { 'order': 'desc' } }];
	const priceAscSort = [{ 'ListPrice': { 'order': 'asc' } }];

	if (propertyListSort === 1) {
		sort = newestSort;
	} else if (propertyListSort === 2) {
		sort = priceDescSort;
	} else if (propertyListSort === 3) {
		sort = priceAscSort;
	} else {
		sort = userFilters.propertyType === 'CapCenterRealty' ||  userFilters.propertyType === 'CapCenterClosed' ? priceDescSort : defaultSort;
	}


	const finalQueryForElastic: any = {
		from: pageNumber * pageSize,
		size: pageSize,
		sort: sort,
		query: {
			bool: boolQuery
		},
		_source: projectionFieldsToReturn('markersAndCards')
	};

	return finalQueryForElastic;
}

export const getFilterFragments = (userLocation: UserLocation, userFilters: UserFilters) => {

	const filters: any = [];

	if (userFilters.bedroomsMin > 0)
		filters.push(getBedroomFilterFragment(userFilters.bedroomsMin));

	if (userFilters.bathroomsMin > 0)
		filters.push(getBathroomFilterFragment(userFilters.bathroomsMin));

	if (userFilters.priceMin > 0 || userFilters.priceMax > 0)
		filters.push(getPriceFilterFragment(userFilters.priceMin, userFilters.priceMax, userFilters.propertyType));

	if (userFilters.squareFeetMin > 0 || userFilters.squareFeetMax > 0)
		filters.push(getRangeFilterFragment('LivingArea', userFilters.squareFeetMin, userFilters.squareFeetMax));

	if (userFilters.yearBuiltMin > 0 || userFilters.yearBuiltMax > 0)
		filters.push(getRangeFilterFragment('YearBuilt', userFilters.yearBuiltMin, userFilters.yearBuiltMax));

	if (userFilters.homeTypes.length > 0)
		filters.push(getHomeTypeFilterFragment(userFilters.homeTypes));

	if (userFilters.lotSizeMin > 0 || userFilters.lotSizeMax > 0)
		filters.push(getRangeFilterFragment('LotSizeSquareFeet', userFilters.lotSizeMin, userFilters.lotSizeMax));

	if (userFilters.parkingSpotMin > 0)
		filters.push(getMinFilterFragment('ParkingTotal', userFilters.parkingSpotMin));

	if (userFilters.hasGarage)
		filters.push(getHasGarageFilterFragment());

	if (userFilters.maxHoaFee >= 0)
		filters.push(getMaxFilterFragment('DerivedAssociationFeeMonthly', userFilters.maxHoaFee));

	// Don't show properties without Listing Price
	filters.push({
		exists: {
			field: 'ListPrice',
		},
	});

	filters.push({
		exists: {
			field: 'location',
		},
	});

	if(userFilters.openHouse){
		filters.push({bool: {
			must: [
				{
					range: {
						'OpenHouse.OpenHouseEndTime': {
							gte: new Date().toISOString()
						}
					}
				},
				{
					exists: {
						field: 'OpenHouse'
					}
				}
			]
		}});
	}

	if (userFilters.propertyType === 'RecentlySold') {
		filters.push(
			{

				bool: {
					should: [
						{
							range: {
								CloseDate: {
									gt: 'now-180d/d',
								},
							},
						},
						{
							range: {
								OffMarketTimestamp: {
									gt: 'now-180d/d',
								},
							},
						},
						{
							range: {
								OnMarketTimestamp: {
									gt: 'now-180d/d',
								},
							},
						}
					]

				}
			}
		);
		filters.push(
			{

				bool: {
					should: [
						{
							term: {
								'StandardStatus.keyword': 'Off Market',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Closed',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Sold',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Pending',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Under Contract',
							}
						}
					],
					'minimum_should_match' : 1
				}
			}
		);
	} else if (userFilters.propertyType === 'CapCenterRealty' || userFilters.propertyType === 'CapCenterClosed') {
		filters.push(
			{

				bool: {
					should: [
						{
							range: {
								CloseDate: {
									gt: 'now-1825d/d',
								},
							},
						},
						{
							range: {
								OnMarketTimestamp: {
									gt: 'now-1825d/d',
								},
							},
						}
					]

				}
			}
		);
		filters.push(
			{
				bool: {
					should: [
						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'REIN'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': '129380',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': '129380',
									}
								}],
								'minimum_should_match' : 1
							}	
						},


						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'ColumbiaSC'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': '1461',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': '1461',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'TriangleMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': '77277',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': '77277',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'CTAR'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': '9452',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': '9452',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'CentralVirginia'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'CAPC01',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'CAPC01',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'FMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'CAPC01',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'CAPC01',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'BrightMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'CIRCB1',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'CIRCB1',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'GeorgiaMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'CPCN01',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'CPCN01',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'BrightMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'CPTT1',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'CPTT1',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'TriadMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'GCAPCEN01',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'GCAPCEN01',
									}
								}],
								'minimum_should_match' : 1
							}
						},


						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'CharlottesvilleVA'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'K10215',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'K10215',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'CentralVirginia'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'LOAN87',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'LOAN87',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'NorthCarolinaRegional'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'o847502762',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'o847502762',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'GreenvilleSC'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': '2392',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': '2392',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'CanopyMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'R00079',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'R00079',
									}
								}],
								'minimum_should_match' : 1
							}
						},

						{
							bool: {
								must: [{
									term: {
										'OriginatingSystemName.keyword': 'CanopyMLS'
									}
								}],
								should: [{
									term: {
										'ListOfficeMlsId.keyword': 'R00080',
									}
								}, {
									term: {
										'BuyerOfficeMlsId.keyword': 'R00080',
									}
								}],
								'minimum_should_match' : 1
							}
						},
					],
					'minimum_should_match' : 1
				}
			}
		);

		filters.push(
			{

				bool: {
					should: [
						{
							term: {
								'StandardStatus.keyword': 'Active',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Coming Soon',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Off Market',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Closed',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Sold',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Pending',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Under Contract',
							}
						}
					],
					'minimum_should_match' : 1
				}
			}
		);
	} else {
		// Only show Active and Coming Soon properties
		filters.push(
			{

				bool: {
					should: [
						{
							term: {
								'StandardStatus.keyword': 'Active',
							}
						},
						{
							term: {
								'StandardStatus.keyword': 'Coming Soon',
							}
						}
					],
					'minimum_should_match' : 1
				}
			}
		);
	}


	if (userLocation.mlsId) {
		filters.push({
			term: { ListingId: userLocation.mlsId },
		});
	}

	return filters;
};


